/**
 * Drawing recovery reports (Faz 9.4, OCS `RecoveryReport` TS subset).
 *
 * Every recovery pass (RECOVER command, .sv$ restore, sniff-open with
 * dropped entities) produces a report and persists it as a plain-text log
 * under `yap/recovery_logs/` (appData on the desktop host, mirrored OPFS
 * in the browser). Report ids follow the OCS scheme: `R2-{unix}-{sha12}`.
 * The source path is masked to `<source-path>` in the log (OCS privacy
 * rule) — the dialog shows the file name only.
 */

import { RECOVERY_LOG_DIR, writeAppFile } from './desktopIo';

export type RecoveryStatus = 'recovered' | 'clean' | 'failed';

export interface RecoveryReport {
    status: RecoveryStatus;
    fileName: string;
    sizeBytes: number;
    sourceSha256: string | null;
    entitiesScanned: number;
    entitiesRemoved: number;
    diagnostics: string[];
    totalMs: number;
    reportId: string;
}

/** `R2-{unix}-{sha12}` — falls back to a length stamp without a hash. */
export function newRecoveryReportId(sourceSha256: string | null, timestamp = Date.now()): string {
    const sha12 = sourceSha256
        ? sourceSha256.slice(0, 12)
        : Math.abs(timestamp).toString(16).padStart(12, '0').slice(0, 12);
    return `R2-${Math.floor(timestamp / 1000)}-${sha12}`;
}

export function createRecoveryReport(input: {
    status: RecoveryStatus;
    fileName: string;
    sizeBytes: number;
    sourceSha256: string | null;
    entitiesScanned: number;
    entitiesRemoved: number;
    diagnostics: string[];
    startedAt: number;
}): RecoveryReport {
    return {
        status: input.status,
        fileName: input.fileName,
        sizeBytes: input.sizeBytes,
        sourceSha256: input.sourceSha256,
        entitiesScanned: input.entitiesScanned,
        entitiesRemoved: input.entitiesRemoved,
        diagnostics: input.diagnostics,
        totalMs: Date.now() - input.startedAt,
        reportId: newRecoveryReportId(input.sourceSha256, input.startedAt),
    };
}

/** Plain-text report body; the source path is masked, never written. */
export function formatRecoveryLog(report: RecoveryReport): string {
    const lines = [
        'Yap Drawing Recovery Report',
        `report id:    ${report.reportId}`,
        `status:       ${report.status}`,
        `file:         ${report.fileName}`,
        'source path:  <source-path>',
        `size bytes:   ${report.sizeBytes}`,
        `sha256:       ${report.sourceSha256 ?? '(unavailable)'}`,
        `entities:     ${report.entitiesScanned} scanned, ${report.entitiesRemoved} removed`,
        `diagnostics:  ${report.diagnostics.length > 0 ? '' : '(none)'}`,
        ...report.diagnostics.map((d) => `  - ${d}`),
        `total ms:     ${report.totalMs}`,
    ];
    return lines.join('\n');
}

/** Persists the report (best-effort: recovery must never fail on logging). */
export async function writeRecoveryLog(report: RecoveryReport): Promise<string | null> {
    try {
        const rel = `${RECOVERY_LOG_DIR}/drawing_recovery_${report.reportId}.log`;
        await writeAppFile(rel, formatRecoveryLog(report));
        return rel;
    } catch {
        return null;
    }
}

/** One-line dialog summary (RecoveryHost / RECOVER notify). */
export function summarizeRecoveryReport(report: RecoveryReport): string {
    return `${report.status.toUpperCase()} ${report.fileName}: ${report.entitiesScanned} scanned, `
        + `${report.entitiesRemoved} removed, ${report.totalMs} ms [${report.reportId}]`;
}
